'use client'
import { type ReactNode, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DesktopComparisonTable } from 'ui/src/components/ComparisonTable/DesktopComparisonTable'
import { MobileComparisonTable } from 'ui/src/components/ComparisonTable/MobileComparisonTable'
import { Switch } from 'ui/src/components/Switch/Switch'
import { xStack, yStack } from 'ui/src/patterns/stack'
import { sprinkles } from 'ui/src/theme/sprinkles.css'
import { useTierComparison } from '@/appComponents/TierComparison/TierComparisonProvider'
import { tierComparisonToTable } from '@/appComponents/TierComparison/tierComparisonToTable'
import { useResponsiveVariant } from '@/utils/useResponsiveVariant'
import { tableHeader, tableWrapper } from './ProductTierComparison.css'

export type ProductTierComparisonProps = {
  coverageTextLabel: string
  title?: ReactNode
  subTitle?: ReactNode
}

export function ProductTierComparison({ title, subTitle, coverageTextLabel }: ProductTierComparisonProps) {
  const { t } = useTranslation()
  const variant = useResponsiveVariant('sm')

  const [isCoverageTextVisible, toggleCoverageText] = useState(false)
  const tierComparison = useTierComparison()
  const { table } = tierComparisonToTable(tierComparison, {
    t,
    showCoverageText: isCoverageTextVisible,
    showPerilColor: true,
  })

  const showCoverageSwitch = useMemo(
    () => tierComparison.rows.some((row) => row.cells.some((cell) => cell.coverageText != null)),
    [tierComparison],
  )

  return (
    <div className={tableWrapper}>
      <div className={yStack({ gap: 'lg' })}>
        <div className={tableHeader}>
          <div>
            {title}
            {subTitle}
          </div>

          {showCoverageSwitch && (
            <div className={xStack({ gap: 'sm', alignItems: 'center' })}>
              <label
                // Optically align the switch with the text
                style={{ marginTop: -2 }}
                htmlFor="coverage-text"
                className={sprinkles({ fontSize: 'sm', color: 'textSecondaryOnGray' })}
              >
                {coverageTextLabel}
              </label>
              <Switch checked={isCoverageTextVisible} onCheckedChange={toggleCoverageText} id="coverage-text" />
            </div>
          )}
        </div>

        {variant === 'mobile' && (
          <MobileComparisonTable {...table} defaultSelectedColumn={getDefaultSelectedColumn(tierComparison)} />
        )}
        {variant === 'desktop' && <DesktopComparisonTable {...table} />}
      </div>
    </div>
  )
}

type TierComparison = ReturnType<typeof useTierComparison>
type TierComparisonProductVariant = TierComparison['variantColumns'][0]

function getDefaultSelectedColumn(tierComparison: TierComparison) {
  // A reasonable assumption here: 'typeOfContract' is the same for all 'variantColumns'
  const product = getProductByTypeOfContract(tierComparison.variantColumns[0].typeOfContract)
  let defaultTier: TierComparisonProductVariant | undefined

  switch (product) {
    case 'RENT':
    case 'BRF':
    case 'HOUSE':
      defaultTier = tierComparison.variantColumns.find((col) => col.termsVersion.includes('MAX'))
      break
    case 'STUDENT':
      defaultTier = tierComparison.variantColumns.find((col) => col.termsVersion.includes('RENT'))
      break
    case 'CAR':
      defaultTier = tierComparison.variantColumns.find((col) => col.termsVersion.includes('FULL'))
      break
    case 'PET':
      defaultTier = tierComparison.variantColumns.find((col) => col.termsVersion.includes('PREMIUM'))
      break
    default:
      break
  }

  return defaultTier?.displayNameSubtype
}

function getProductByTypeOfContract(typeOfContract: string) {
  if (typeOfContract.includes('SE_APARTMENT_RENT')) {
    return 'RENT'
  } else if (typeOfContract.includes('SE_APARTMENT_BRF')) {
    return 'BRF'
  } else if (typeOfContract.includes('SE_HOUSE')) {
    return 'HOUSE'
  } else if (typeOfContract.includes('SE_APARTMENT_STUDENT')) {
    return 'STUDENT'
  } else if (typeOfContract.includes('SE_CAR')) {
    return 'CAR'
  } else if (typeOfContract.includes('SE_DOG') || typeOfContract.includes('SE_CAT')) {
    return 'PET'
  }

  return 'OTHER'
}
